import { z } from 'zod';
import type { FlowSpec } from './flow.js';
import { MCP_PRESETS_BY_ID } from './mcp-catalog.js';

/**
 * Which agent should run a flow? Deterministic scoring, no model call. What matters:
 *  - integrations: agents that already bind the flow's MCP servers need no new access;
 *  - cost: `mcp_call` steps cost nothing on any agent and `llm` steps always use the org's cheapest
 *    model, so the agent's own model/runtime only matters for `agent`/`browser` steps — there a
 *    Claude subscription ($0 per token) or the cheapest tool-capable model wins;
 *  - least privilege: among equal agents the one with the fewest extra integrations;
 *  - browser steps need their hosts on the agent's egress allowlist;
 *  - role fit: words of the flow name/description in the agent's role and description.
 */

export const SUGGESTION_REASONS = [
  'covers_servers',
  'already_attached',
  'deterministic',
  'subscription',
  'cheap_model',
  'least_privilege',
  'role_fit',
  'no_tools_model',
] as const;
export const SUGGESTION_MISSING = ['bind_server', 'connect_server', 'egress_host'] as const;

export const AgentSuggestion = z.object({
  agentId: z.string(),
  score: z.number(),
  reasons: z.array(z.object({ code: z.enum(SUGGESTION_REASONS), detail: z.string().optional() })),
  missing: z.array(z.object({ code: z.enum(SUGGESTION_MISSING), detail: z.string() })),
});
export type AgentSuggestion = z.infer<typeof AgentSuggestion>;

export const NewAgentSuggestion = z.object({
  runtime: z.enum(['ai-sdk', 'claude-agent-sdk', 'codex-sdk']),
  modelRef: z.string().nullable(),
  reason: z.enum(['deterministic', 'subscription', 'cheap_model']),
});
export type NewAgentSuggestion = z.infer<typeof NewAgentSuggestion>;

export const FlowSuggestionsDto = z.object({
  agents: z.array(AgentSuggestion),
  newAgent: NewAgentSuggestion,
  needs: z.object({
    serverIds: z.array(z.string()),
    presetIds: z.array(z.string()),
    modelSteps: z.number(),
    browserHosts: z.array(z.string()),
  }),
});
export type FlowSuggestionsDto = z.infer<typeof FlowSuggestionsDto>;

export interface FlowNeeds {
  /** Org servers the flow's MCP steps use (resolved ids). */
  serverIds: string[];
  /** Presets used by steps whose server is not connected in the org. */
  presetIds: string[];
  /** `agent` + `browser` steps: the only ones that run on the agent's own model/runtime. */
  modelSteps: number;
  browserHosts: string[];
}

export function flowNeeds(spec: FlowSpec, servers: { id: string; presetId: string | null }[]): FlowNeeds {
  const serverIds = new Set<string>();
  const presetIds = new Set<string>();
  const hosts = new Set<string>();
  let modelSteps = 0;
  for (const s of spec.steps) {
    if (s.kind === 'mcp_call') {
      const srv =
        servers.find((x) => x.id === s.server.mcpServerId) ??
        (s.server.presetId ? servers.find((x) => x.presetId === s.server.presetId) : undefined);
      if (srv) serverIds.add(srv.id);
      else if (s.server.presetId) presetIds.add(s.server.presetId);
    } else if (s.kind === 'agent') modelSteps++;
    else if (s.kind === 'browser') {
      modelSteps++;
      for (const h of s.egress) hosts.add(h);
    }
  }
  return { serverIds: [...serverIds], presetIds: [...presetIds], modelSteps, browserHosts: [...hosts] };
}

export interface SuggestAgent {
  id: string;
  name: string;
  roleTitle: string;
  description: string;
  status: string;
  kind: string;
  runtime: string;
  modelRef: string;
  mcpServerIds: string[];
  flowIds: string[];
  egressAllowlist: string[];
}

export interface SuggestModel {
  modelRef: string;
  supportsTools: boolean;
  /** Typical small call cost; null when unpriced. */
  estCallUsd: number | null;
  /** The provider runs Claude Code on a subscription (subscription / hybrid). */
  subscription: boolean;
  providerType: string;
}

const wordsOf = (s: string) =>
  new Set(
    s
      .toLowerCase()
      .split(/[^a-z0-9à-ÿ]+/)
      .filter((w) => w.length > 2),
  );

const hostAllowed = (host: string, allow: string[]) =>
  allow.some((a) => a === host || (a.startsWith('*.') && (host === a.slice(2) || host.endsWith(a.slice(1)))));

export function suggestAgents(
  flow: { id: string; name: string; description: string },
  needs: FlowNeeds,
  agents: SuggestAgent[],
  models: SuggestModel[],
  serverNames: Map<string, string>,
): AgentSuggestion[] {
  const byRef = new Map(models.map((m) => [m.modelRef, m]));
  const priced = models.filter((m) => m.estCallUsd !== null).map((m) => m.estCallUsd as number);
  const maxCost = priced.length ? Math.max(...priced) : 0;
  const flowWords = wordsOf(`${flow.name} ${flow.description}`);
  const out: AgentSuggestion[] = [];
  for (const a of agents) {
    if (a.status !== 'active' || a.kind !== 'persistent') continue;
    const reasons: AgentSuggestion['reasons'] = [];
    const missing: AgentSuggestion['missing'] = [];
    let score = 0;
    const bound = new Set(a.mcpServerIds);
    const need = needs.serverIds;
    const covered = need.filter((id) => bound.has(id));
    if (need.length) {
      score += 40 * (covered.length / need.length);
      if (covered.length)
        reasons.push({ code: 'covers_servers', detail: `${covered.length}/${need.length}` });
      for (const id of need)
        if (!bound.has(id)) missing.push({ code: 'bind_server', detail: serverNames.get(id) ?? id });
    } else score += 20;
    for (const p of needs.presetIds)
      missing.push({ code: 'connect_server', detail: MCP_PRESETS_BY_ID.get(p)?.name ?? p });
    if (a.flowIds.includes(flow.id)) {
      score += 10;
      reasons.push({ code: 'already_attached' });
    }
    const extra = a.mcpServerIds.filter((id) => !need.includes(id)).length;
    const m = byRef.get(a.modelRef);
    if (needs.modelSteps === 0) {
      score += 15;
      reasons.push({ code: 'deterministic' });
      score += 15 * (3 / (extra + 3));
      if (extra === 0) reasons.push({ code: 'least_privilege' });
    } else {
      if (m && !m.supportsTools) {
        score -= 30;
        reasons.push({ code: 'no_tools_model' });
      }
      if (a.runtime === 'claude-agent-sdk' && m?.subscription) {
        score += 20;
        reasons.push({ code: 'subscription' });
      } else if (m?.estCallUsd != null && maxCost > 0) {
        score += 10 * (1 - m.estCallUsd / maxCost);
        if (m.estCallUsd <= Math.min(...priced) * 1.5) reasons.push({ code: 'cheap_model' });
      }
      score += 5 * (3 / (extra + 3));
      for (const h of needs.browserHosts)
        if (!hostAllowed(h, a.egressAllowlist)) missing.push({ code: 'egress_host', detail: h });
    }
    const roleWords = wordsOf(`${a.roleTitle} ${a.description}`);
    const fit = [...flowWords].filter((w) => roleWords.has(w)).length;
    if (fit) {
      score += Math.min(10, fit * 4);
      reasons.push({ code: 'role_fit' });
    }
    score -= 5 * missing.length;
    out.push({ agentId: a.id, score: Math.round(score * 10) / 10, reasons, missing });
  }
  return out.sort((x, y) => y.score - x.score);
}

/** Runtime + model for a dedicated new agent for this flow. */
export function suggestNewAgent(needs: FlowNeeds, models: SuggestModel[]): NewAgentSuggestion {
  const tools = models.filter((m) => m.supportsTools);
  const cheapest = [...tools].sort(
    (a, b) => (a.estCallUsd ?? Number.POSITIVE_INFINITY) - (b.estCallUsd ?? Number.POSITIVE_INFINITY),
  )[0];
  if (needs.modelSteps === 0)
    return {
      runtime: 'ai-sdk',
      modelRef: cheapest?.modelRef ?? models[0]?.modelRef ?? null,
      reason: 'deterministic',
    };
  const sub = tools.find((m) => m.subscription && m.providerType === 'anthropic');
  if (sub) return { runtime: 'claude-agent-sdk', modelRef: sub.modelRef, reason: 'subscription' };
  return { runtime: 'ai-sdk', modelRef: cheapest?.modelRef ?? null, reason: 'cheap_model' };
}
